import { Img } from "../image";
import { Link } from "@/i18n/navigation";
import { cn } from "@/lib/utils";
import { type HTMLAttributes, type ReactNode } from "react";

type ListingItemImageResponsiveSource = {
  src: string;
  media?: string;
  minWidth?: number;
  maxWidth?: number;
};

const getResponsiveSourceMedia = ({
  media,
  minWidth,
  maxWidth,
}: ListingItemImageResponsiveSource) => {
  if (media) {
    return media;
  }

  if (typeof minWidth === "number" && typeof maxWidth === "number") {
    return `(min-width: ${minWidth}px) and (max-width: ${maxWidth}px)`;
  }

  if (typeof minWidth === "number") {
    return `(min-width: ${minWidth}px)`;
  }

  if (typeof maxWidth === "number") {
    return `(max-width: ${maxWidth}px)`;
  }

  return undefined;
};

type ListingItemImageProps = HTMLAttributes<HTMLDivElement> & {
  name: string;
  imageUrl: string | ListingItemImageResponsiveSource[];
  imageClassName?: string;
  url?: string;
  size?: "small" | "default";
  prioritized?: boolean;
};

export const ListingItemImage = ({
  name,
  imageUrl,
  className,
  imageClassName,
  children,
  url,
  size = "default",
  prioritized = false,
  ...props
}: ListingItemImageProps) => {
  const styling = cn(
    "bg-brand-grey2 relative aspect-video w-full flex items-center justify-center rounded-tl-lg rounded-tr-lg md:w-60 md:aspect-auto md:rounded-bl-lg md:rounded-tr-none md:rounded-br-none",
    className
  );
  const imageSources = Array.isArray(imageUrl)
    ? imageUrl
        .map((source) => ({
          ...source,
          media: getResponsiveSourceMedia(source),
        }))
        .filter((source) => source.src)
    : [];
  const fallbackImageUrl = Array.isArray(imageUrl)
    ? (imageSources.find((source) => !source.media)?.src ?? imageSources.at(-1)?.src)
    : imageUrl;

  const Wrapper = ({ children }: { children: ReactNode }) =>
    url ? (
      <Link href={url} className={styling}>
        {children}
      </Link>
    ) : (
      <div className={styling} {...props}>
        {children}
      </div>
    );

  return (
    <Wrapper>
      {fallbackImageUrl &&
        (imageSources.length > 0 ? (
          <picture className="contents">
            {imageSources
              .filter((source) => source.media)
              .map((source) => (
                <source
                  key={`${source.media}-${source.src}`}
                  media={source.media}
                  srcSet={source.src}
                />
              ))}
            <Img
              src={fallbackImageUrl}
              alt={name}
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              className={cn(
                "rounded-tl-lg rounded-tr-lg object-cover object-center md:rounded-bl-lg md:rounded-tr-none md:rounded-br-none",
                imageClassName
              )}
            />
          </picture>
        ) : (
        <Img
          src={fallbackImageUrl}
          alt={name}
          fill
          fetchPriority={prioritized ? "high" : "auto"}
          loading={prioritized ? "eager" : "lazy"}
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          className={cn(
            "rounded-tl-lg rounded-tr-lg object-cover object-center md:rounded-bl-lg md:rounded-tr-none md:rounded-br-none",
            imageClassName
          )}
        />
        ))}
      {children}
    </Wrapper>
  );
};
